import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import { format, previousSaturday, startOfQuarter, sub } from 'date-fns';
import { NeuInput } from '@neutron/react';
import { date as dt, unCamelCase } from '../../../../utils/helpers';
import { Filter } from '../../../../config/interfaces';
import {
  Range,
  InputHeading
} from '../../../searchHeader/complianceHeader/ComplianceHeader.styles';
import { Dropdown } from '../../../shared/dropdowns';

interface ComplianceFilterSectionProps {
  filterValues: Filter;
  setFilterValues: Dispatch<SetStateAction<Filter>>;
}
const dateRanges = [
  { value: 'custom', displayName: 'Custom' },
  { value: 'today', displayName: 'Today' },
  { value: 'monthToDate', displayName: 'Month to date' },
  { value: 'lastFullMonth', displayName: 'Last full month' },
  { value: 'lastFullQuarter', displayName: 'Last full quarter' }
];

const ComplianceFilterSection: FC<ComplianceFilterSectionProps> = ({
  filterValues,
  setFilterValues
}) => {
  const [reportDateRangeType, setReportDateRangeType] =
    useState<string>('Today');
  const [showCustomDate, setShowCustomDate] = useState<boolean>(
    filterValues.dateRangeText === 'Custom'
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (filterValues.dateRangeText === 'Custom') {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
    }
    setReportDateRangeType(filterValues.dateRangeText || 'Today');
  }, [filterValues]);

  const handleSelects = (e: any) => {
    const { name, value } = e.target as HTMLInputElement;
    return setFilterValues({
      ...filterValues,
      dateRangeText: 'Custom',
      [name]: value
    });
  };

  const handleDateRangeSelect = (range: {
    value: string;
    displayName: string;
  }) => {
    const selectedRange = range.value;
    setReportDateRangeType(range.displayName);

    const today = new Date();

    if (selectedRange === 'today') {
      setShowCustomDate(false);

      const dayToday = format(
        new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: dayToday,
        endDate: dayToday,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'monthToDate') {
      setShowCustomDate(false);

      const firstDayOfMonth = format(
        new Date(today.getFullYear(), today.getMonth(), 1),
        'yyyy-MM-dd'
      );

      const dayToday = format(
        new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: firstDayOfMonth,
        endDate: dayToday,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'lastFullWeek') {
      setShowCustomDate(false);
      const lastSaturday = previousSaturday(new Date());
      const lastSaturdayFormatted = format(lastSaturday, 'yyyy-MM-dd');

      const secondLastSunday = format(
        new Date(
          lastSaturday.getFullYear(),
          lastSaturday.getMonth(),
          lastSaturday.getDate() - 6
        ),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: secondLastSunday,
        endDate: lastSaturdayFormatted,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'lastFullMonth') {
      setShowCustomDate(false);

      const lastMonthFirstDay = format(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        'yyyy-MM-dd'
      );

      const lastMonthLastDay = format(
        sub(new Date(today.getFullYear(), today.getMonth(), 1), { days: 1 }),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: lastMonthFirstDay,
        endDate: lastMonthLastDay,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'lastFullQuarter') {
      setShowCustomDate(false);

      const lastQuarterLastDay = sub(startOfQuarter(today), { days: 1 });
      const lastQuarterLastDayFormatted = format(
        lastQuarterLastDay,
        'yyyy-MM-dd'
      );
      const lastQuarterFirstDay = format(
        startOfQuarter(
          new Date(
            lastQuarterLastDay.getFullYear(),
            lastQuarterLastDay.getMonth(),
            lastQuarterLastDay.getDate() - 1
          )
        ),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: lastQuarterFirstDay,
        endDate: lastQuarterLastDayFormatted,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'custom') {
      setShowCustomDate(true);
    }
  };

  const minStartDate = useMemo(
    () => dt.convertToDayOfYear(sub(new Date(), { years: 2 })),
    []
  );
  const maxStartDate = useMemo(
    () =>
      filterValues.endDate &&
      dt.convertToDayOfYear(dt.localDayOfYearDate(filterValues.endDate)),
    [filterValues.endDate]
  );
  const minEndDate = useMemo(
    () =>
      filterValues.startDate &&
      dt.convertToDayOfYear(dt.localDayOfYearDate(filterValues.startDate)),
    [filterValues.startDate]
  );
  const maxEndDate = useMemo(() => dt.convertToDayOfYear(new Date()), []);

  return (
    <div id="Compliance-Filter-Section" data-testid="compliance-filter-section">
      <Range>
        <InputHeading>Date Range</InputHeading>
        <Dropdown
          classes="ps-0 pt-0"
          name="date-range"
          open={open}
          placeholder={unCamelCase(reportDateRangeType)}
          setOpen={setOpen}
        >
          {dateRanges.map(range => (
            <div
              key={range.value}
              aria-selected={unCamelCase(range.value) === reportDateRangeType}
              className={`dropdown-hover-item${
                unCamelCase(range.value) === reportDateRangeType
                  ? ' selected'
                  : ''
              }`}
              role="option"
              tabIndex={0}
              onClick={() => handleDateRangeSelect(range)}
              onKeyDown={() => handleDateRangeSelect(range)}
            >
              {range.displayName}
            </div>
          ))}
        </Dropdown>
      </Range>
      {showCustomDate ? (
        <div>
          <NeuInput
            id="start-date"
            required
            label="Starting"
            type="date"
            name="startDate"
            onNeuChange={handleSelects}
            value={filterValues.startDate}
            min={minStartDate}
            max={maxStartDate}
          />
          <NeuInput
            id="end-date"
            required
            label="Ending"
            type="date"
            name="endDate"
            onNeuChange={handleSelects}
            value={filterValues.endDate}
            min={minEndDate}
            max={maxEndDate}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ComplianceFilterSection;
